/**
 * Admin 风控台 API（/admin/api/v1/risk/*）
 * 真联调：VITE_USE_MOCK=false
 */
import {
  adminDelete, adminDownload, adminGet, adminPatch, adminPost, adminPut, adminUpload, USE_MOCK,
} from './client';

// ---- 后端列表行（与 RiskAppService to*Map 对齐）----

export type RiskMetrics = {
  totalTransactions?: string;
  interceptedCount?: string;
  blockRate?: string;
  latestBlocked?: string;
  alertsUrgent?: string;
};

export type RiskIntercept = {
  id: number;
  eventNo: string;
  transactionId?: number | null;
  cardholderId: number;
  merchantId: number;
  merchantName?: string | null;
  cardId?: number | null;
  amount?: string;
  currency?: string;
  triggerType: 'RULE' | 'MODEL' | 'LIST';
  riskScore?: string;
  matchedRuleIds: number[];
  occurredAt: string;
  status: 'TRIGGERED' | 'REVIEWING' | 'RELEASED' | 'BLOCKED' | 'FALSE_POSITIVE';
  actionTaken?: 'BLOCK' | 'FORCE_3DS' | 'ALERT' | 'FREEZE_CARD' | 'CANCEL_CARD' | null;
  reviewerId?: string | null;
  reviewedAt?: string | null;
  reviewComment?: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
};

/**
 * AI 建议状态（与后端契约对齐，无 PENDING）：
 * READY=有建议；NO_PROVIDER=未配置供应商；NOT_ELIGIBLE=门闩不满足；FAILED=预审失败/超时。
 * 非 READY 时 aiAdvice 必须为 null（键存在）。
 */
export type AiRiskAdviceStatus = 'READY' | 'NO_PROVIDER' | 'NOT_ELIGIBLE' | 'FAILED';

/** 只读建议动作（≠ 处置 action；页面禁读/禁高亮处置下拉） */
export type AiSuggestedAction = 'RELEASE' | 'BLOCK' | 'FALSE_POSITIVE' | 'REVIEW';

/** READY 建议对象；confidence 为 decimal string（scale 4，如 0.9500），禁 Number 再运算 */
export type AiRiskAdvice = {
  suggestedAction: AiSuggestedAction;
  reason: string | null;
  confidence: string;
  riskFactors: string[];
};

/**
 * 拦截详情视图：在列表行基础上并列 aiAdviceStatus + aiAdvice（可选，容忍后端缺键）。
 * 列表行 RiskIntercept 不含建议字段（键省略非 null，AC-RISK-EVT-001b）。
 */
export type RiskInterceptDetail = RiskIntercept & {
  aiAdviceStatus?: AiRiskAdviceStatus;
  aiAdvice?: AiRiskAdvice | null;
};

type RiskInterceptPage = { total: number; items: RiskIntercept[] };
export type RiskDispositionAction = 'RELEASE' | 'BLOCK' | 'WHITELIST' | 'FALSE_ALARM';
type RiskInterceptDisposeResult = {
  id: number;
  eventNo: string;
  status: RiskIntercept['status'];
  action: RiskDispositionAction;
  reviewedAt: string;
};

export type AdminRiskRuleCategory = 'VELOCITY' | 'AML' | 'OPEN_CARD' | 'WITHDRAW'
  | 'DEPOSIT' | 'DEVICE' | 'GEO' | 'MCC' | 'BATCH_OPS';
export type AdminRiskRuleType = 'THRESHOLD' | 'LIST' | 'TIME_WINDOW' | 'SCORE' | 'CUSTOM' | 'BATCH';
export type AdminRiskRuleStatus = 'DRAFT' | 'SHADOW' | 'LIVE' | 'DISABLED';
export type AdminRiskHitAction = 'BLOCK' | 'FORCE_3DS' | 'ALERT' | 'REVIEW' | 'FREEZE_CARD' | 'CANCEL_CARD';
export type AdminRiskRule = {
  ruleId: string;
  ruleCode: string;
  scope: 'GLOBAL' | 'MERCHANT';
  merchantId: string | null;
  parentRuleId: string | null;
  category: AdminRiskRuleCategory;
  ruleType: AdminRiskRuleType;
  params: Record<string, unknown>;
  comboLogic: 'AND' | 'OR' | null;
  ruleGroup: string | null;
  effectiveScope: unknown;
  priority: number;
  status: AdminRiskRuleStatus;
  hitAction: AdminRiskHitAction;
  editable: boolean;
  version: number;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
};
type AdminRiskRuleCategorySummary = {
  category: AdminRiskRuleCategory;
  liveCount: number;
  totalCount: number;
};
export type AdminRiskRulePage = {
  total: number;
  items: AdminRiskRule[];
  categories: AdminRiskRuleCategorySummary[];
  page: number;
  size: number;
};
type AdminRiskRuleFilters = {
  category?: AdminRiskRuleCategory;
  scope?: 'GLOBAL' | 'MERCHANT';
  status?: AdminRiskRuleStatus;
  priority?: number;
  merchantId?: string;
  page?: number;
  size?: number;
};
type AdminRiskRuleWrite = {
  ruleCode?: string;
  category?: AdminRiskRuleCategory;
  ruleType?: AdminRiskRuleType;
  params?: Record<string, unknown>;
  comboLogic?: 'AND' | 'OR' | null;
  ruleGroup?: string | null;
  effectiveScope?: unknown;
  priority?: number;
  hitAction?: AdminRiskHitAction;
  status?: AdminRiskRuleStatus;
  editable?: boolean;
};
type AdminRiskRulePromote = {
  ruleId: string;
  hitRate: string;
  falsePositiveRate: string;
  sampleSize: number;
  hitCount: number;
  passed: boolean;
  status: AdminRiskRuleStatus;
  version: number;
};

export type RiskBlocklistType = 'BLACK' | 'WHITE';
export type RiskBlocklistTargetType = 'CARDHOLDER' | 'MCC' | 'COUNTRY' | 'IP' | 'DEVICE' | 'PHONE';
export type RiskBlocklistSource = 'MANUAL' | 'RULE_AUTO';
export type RiskBlocklistExpiry = 'PERMANENT' | 'ACTIVE' | 'EXPIRED';
export type RiskBlocklistEntry = {
  listId: string;
  listType: RiskBlocklistType;
  targetType: RiskBlocklistTargetType;
  targetValue: string;
  source: RiskBlocklistSource;
  ruleId?: string | null;
  reason: string;
  createdBy: string;
  expiresAt?: string | null;
  expired: boolean;
  createdAt: string;
};
export type RiskBlocklistPage = {
  total: number;
  items: RiskBlocklistEntry[];
  page: number;
  pageSize: number;
};
type RiskBlocklistFilters = {
  listType?: RiskBlocklistType | '';
  targetType?: RiskBlocklistTargetType | '';
  source?: RiskBlocklistSource | '';
  expiry?: RiskBlocklistExpiry | '';
  q?: string;
  page?: number;
  pageSize?: number;
};
export type RiskBlocklistWrite = {
  listType: RiskBlocklistType;
  targetType: RiskBlocklistTargetType;
  targetValue: string;
  expiresAt?: string | null;
  reason: string;
};
type RiskBlocklistImportError = { line: number; reason: string };
export type RiskBlocklistWriteResult = {
  successCount: number;
  skippedCount: number;
  failCount: number;
  errors: RiskBlocklistImportError[];
};

export type AmlCase = {
  strId: string;
  transactionId: string;
  transactionNo?: string | null;
  cardholderId: string;
  merchantId: string;
  cardId: string;
  subjectName: string;
  amount: string;
  currency: string;
  type: 'CTR' | 'STR' | 'SAR';
  status: 'PENDING' | 'FILED' | 'EXCLUDED';
  jurisdiction: string;
  reasonCode: string;
  reasonDetail: string;
  detectionRuleIds: number[];
  deadlineAt: string;
  reviewerId?: string | null;
  reviewerName?: string | null;
  decisionComment?: string | null;
  regulatoryReference?: string | null;
  regulatoryReceiptJson?: string | null;
  filedAt?: string | null;
  excludedAt?: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
};

type AmlCasePage = { total: number; items: AmlCase[] };
type AmlCaseFilters = {
  status?: AmlCase['status'] | '';
  type?: AmlCase['type'] | '';
  merchantId?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
};

export type DisputeRow = {
  disputeId: string;
  transactionId?: string | null;
  txId?: string | null;
  cardholderId: string;
  merchantId: string;
  cardId: string;
  amount: string;
  currency: string;
  reasonCode: string;
  reason: string;
  priority: 'P1' | 'P2' | 'P3' | 'P4';
  status: 'OPEN' | 'INVESTIGATING' | 'WON' | 'LOST';
  assigneeId?: string | null;
  assignee?: string | null;
  evidenceScore: number;
  evidenceRefs: string[];
  deadline: string;
  resolutionComment?: string | null;
  assignedAt?: string | null;
  investigatingAt?: string | null;
  resolvedAt?: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
};

type DisputePage = { total: number; items: DisputeRow[] };
type DisputeFilters = {
  status?: DisputeRow['status'] | '';
  from?: string;
  to?: string;
  /** 商户内部数字 id 的十进制字符串（禁 number：19 位雪花经 Number() 往返丢尾数） */
  merchantId?: string;
  page?: number;
  size?: number;
};

export type ThreeDsSession = {
  id: string;
  sessionId: string;
  cardId: string;
  transactionId?: string | null;
  merchantId: string;
  merchantName: string;
  panMasked: string;
  amount: string;
  currency: string;
  status: 'PENDING' | 'SUCCESS' | 'FAILED' | 'TIMEOUT';
  acsHost?: string | null;
  resultCode?: string | null;
  errorCode?: string | null;
  feeChargeId?: string | null;
  createdAt: string;
  expiresAt: string;
  completedAt?: string | null;
  durationSeconds?: number | null;
  version: number;
};

type ThreeDsSessionPage = { total: number; items: ThreeDsSession[] };
type ThreeDsFilters = {
  status?: ThreeDsSession['status'] | '';
  merchantId?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
};
type ThreeDsExport = {
  contentType: string;
  fileName: string;
  csv: string;
  recordCount: number;
  generatedAt: string;
};

export type FraudGang = {
  gangId: string;
  name: string;
  accountCount: number;
  level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  evidence: string;
  status: 'OPEN' | 'CONFIRMED' | 'CLOSED';
  freezeReason?: string | null;
  confirmedAt?: string | null;
  frozenAt?: string | null;
  closedAt?: string | null;
  version: number;
};

// ---- mock ----

const mockIntercepts: RiskIntercept[] = [
  {
    id: 1001,
    eventNo: 'RIE-1001',
    transactionId: 9001,
    cardholderId: 501,
    merchantId: 20,
    amount: '1200.00',
    currency: 'USD',
    triggerType: 'RULE',
    riskScore: '92',
    matchedRuleIds: [101, 102],
    occurredAt: '2026-08-05T10:00:00',
    status: 'TRIGGERED',
    actionTaken: 'ALERT',
    version: 0,
    createdAt: '2026-08-05T10:00:00',
    updatedAt: '2026-08-05T10:00:00',
  },
  {
    id: 1002,
    eventNo: 'RIE-1002',
    transactionId: 9002,
    cardholderId: 502,
    merchantId: 21,
    amount: '88.50',
    currency: 'USD',
    triggerType: 'MODEL',
    riskScore: '71',
    matchedRuleIds: [],
    occurredAt: '2026-08-05T09:30:00',
    status: 'BLOCKED',
    actionTaken: 'BLOCK',
    reviewerId: '90001',
    reviewedAt: '2026-08-05T09:35:00',
    reviewComment: '确认异常交易',
    version: 1,
    createdAt: '2026-08-05T09:30:00',
    updatedAt: '2026-08-05T09:35:00',
  },
  {
    id: 1003,
    eventNo: 'RIE-1003',
    transactionId: 9003,
    cardholderId: 503,
    merchantId: 22,
    amount: '450.00',
    currency: 'USD',
    triggerType: 'LIST',
    riskScore: '66',
    matchedRuleIds: [201],
    occurredAt: '2026-08-05T09:00:00',
    status: 'TRIGGERED',
    actionTaken: 'ALERT',
    version: 0,
    createdAt: '2026-08-05T09:00:00',
    updatedAt: '2026-08-05T09:00:00',
  },
  {
    id: 1004,
    eventNo: 'RIE-1004',
    transactionId: 9004,
    cardholderId: 504,
    merchantId: 23,
    amount: '66.00',
    currency: 'USD',
    triggerType: 'MODEL',
    riskScore: '88',
    matchedRuleIds: [301, 302],
    occurredAt: '2026-08-05T08:30:00',
    status: 'TRIGGERED',
    actionTaken: 'BLOCK',
    version: 0,
    createdAt: '2026-08-05T08:30:00',
    updatedAt: '2026-08-05T08:30:00',
  },
];

/**
 * mock 详情建议字段（独立映射：列表行 mock 仍不含建议字段，满足 AC-PAGE-PLT-RISK-001-09）。
 * 仅演示用——USE_MOCK 恒为 false，真实联调不会执行。
 * 四态样例：1001=READY（含完整建议）、1003=NO_PROVIDER、1002=NOT_ELIGIBLE（终态无库存）、1004=FAILED。
 */
const mockInterceptDetailAdvice: Record<number, { aiAdviceStatus: AiRiskAdviceStatus; aiAdvice: AiRiskAdvice | null }> = {
  1001: {
    aiAdviceStatus: 'READY',
    aiAdvice: {
      suggestedAction: 'REVIEW',
      reason: '命中地域与速率组合规则，建议人工复核交易环境',
      confidence: '0.9500',
      riskFactors: ['GEO_HIGH_RISK', 'VELOCITY_ANOMALY'],
    },
  },
  1002: {
    aiAdviceStatus: 'NOT_ELIGIBLE',
    aiAdvice: null,
  },
  1003: {
    aiAdviceStatus: 'NO_PROVIDER',
    aiAdvice: null,
  },
  1004: {
    aiAdviceStatus: 'FAILED',
    aiAdvice: null,
  },
};

const mockAml: AmlCase[] = [
  {
    strId: 'AML-2001', transactionId: '80001', transactionNo: 'TX-80001',
    cardholderId: '501', merchantId: '20', cardId: '60001', subjectName: 'Example Trading LLC',
    type: 'CTR', amount: '25000.00000000',
    currency: 'USD',
    status: 'PENDING',
    jurisdiction: 'US', reasonCode: 'AMOUNT_THRESHOLD', reasonDetail: '单笔金额达到 CTR 阈值',
    detectionRuleIds: [101], deadlineAt: '2026-08-06T08:00:00', version: 0,
    createdAt: '2026-08-05T08:00:00',
    updatedAt: '2026-08-05T08:00:00',
  },
  {
    strId: 'AML-2002', transactionId: '80002', transactionNo: 'TX-80002',
    cardholderId: '502', merchantId: '21', cardId: '60002', subjectName: 'Demo Services Ltd',
    type: 'STR',
    amount: '9800.00000000',
    currency: 'USD',
    status: 'PENDING',
    jurisdiction: 'HK', reasonCode: 'STRUCTURING', reasonDetail: '疑似拆分交易规避阈值',
    detectionRuleIds: [102, 103], deadlineAt: '2026-09-03T16:00:00', version: 0,
    createdAt: '2026-08-04T16:00:00',
    updatedAt: '2026-08-04T16:00:00',
  },
];

const mockDisputes: DisputeRow[] = [
  {
    disputeId: 'DSP-3001',
    transactionId: '7001',
    txId: 'TX-7001',
    cardholderId: '501',
    merchantId: '20',
    cardId: '601',
    amount: '299.99',
    currency: 'USD',
    reasonCode: 'FRAUD',
    reason: '持卡人否认该笔交易',
    priority: 'P2',
    status: 'OPEN',
    evidenceScore: 65,
    evidenceRefs: ['auth:7001'],
    deadline: '2026-12-03T07:00:00',
    version: 0,
    createdAt: '2026-08-05T07:00:00',
    updatedAt: '2026-08-05T07:00:00',
  },
];

const mock3ds: ThreeDsSession[] = [
  {
    id: '4001',
    sessionId: '3DS-4001',
    cardId: '6001',
    transactionId: '7001',
    merchantId: '20',
    merchantName: 'Example Trading LLC',
    panMasked: '******1234',
    amount: '120.00',
    currency: 'USD',
    status: 'PENDING',
    createdAt: '2026-08-05T11:00:00',
    expiresAt: '2026-08-05T11:05:00',
    acsHost: 'acs.example.test',
    version: 0,
  },
  {
    id: '4002',
    sessionId: '3DS-4002',
    cardId: '6002',
    transactionId: '7002',
    merchantId: '21',
    merchantName: 'Demo Services Ltd',
    panMasked: '******9876',
    amount: '88.50',
    currency: 'USD',
    status: 'FAILED',
    acsHost: 'acs.demo.test',
    resultCode: 'CHALLENGE_FAILED',
    errorCode: 'ACS_REJECTED',
    createdAt: '2026-08-05T10:00:00',
    expiresAt: '2026-08-05T10:05:00',
    completedAt: '2026-08-05T10:00:42',
    durationSeconds: 42,
    version: 1,
  },
];

const mockFraudGangs: FraudGang[] = [{
  gangId: '71001',
  name: '共享设备团伙',
  accountCount: 6,
  level: 'CRITICAL',
  evidence: '同设备在短时段关联多个持卡人账户',
  status: 'OPEN',
  version: 0,
}];

let mockBlocklistSequence = 950003;
const mockBlocklist: RiskBlocklistEntry[] = [
  {
    listId: '950001', listType: 'BLACK', targetType: 'IP', targetValue: '203.0.113.0/24',
    source: 'MANUAL', reason: '攻击流量来源段', createdBy: '90001', expiresAt: '2026-12-31T23:59:59',
    expired: false, createdAt: '2026-08-08T10:00:00',
  },
  {
    listId: '950002', listType: 'BLACK', targetType: 'CARDHOLDER', targetValue: '501',
    source: 'RULE_AUTO', ruleId: '900001', reason: '拦截事件 RIE-1002 自动联动', createdBy: 'system',
    expiresAt: null, expired: false, createdAt: '2026-08-08T09:00:00',
  },
  {
    listId: '950003', listType: 'WHITE', targetType: 'COUNTRY', targetValue: 'HK',
    source: 'MANUAL', reason: '历史临时放行样例', createdBy: '90001', expiresAt: '2026-08-01T00:00:00',
    expired: true, createdAt: '2026-07-01T00:00:00',
  },
];

// ---- API ----

export async function getRiskMetrics() {
  return adminGet<RiskMetrics>('/risk/metrics/realtime', () => ({
    totalTransactions: '1000',
    interceptedCount: String(mockIntercepts.length),
    blockRate: '0.20%',
    latestBlocked: String(mockIntercepts[0]?.transactionId ?? ''),
    alertsUrgent: String(mockIntercepts.length),
  }));
}

type RiskInterceptFilters = {
  status?: RiskIntercept['status'] | '';
  occurredFrom?: string;
  occurredTo?: string;
  /** 商户内部数字 id 的十进制字符串（禁 number：19 位雪花经 Number() 往返丢尾数） */
  merchantId?: string;
  page?: number;
  pageSize?: number;
};

export async function listIntercepts(filters: RiskInterceptFilters = {}) {
  const q = new URLSearchParams({
    page: String(filters.page ?? 1),
    pageSize: String(filters.pageSize ?? 50),
  });
  if (filters.status) q.set('status', filters.status);
  if (filters.occurredFrom) q.set('occurredFrom', filters.occurredFrom);
  if (filters.occurredTo) q.set('occurredTo', filters.occurredTo);
  if (filters.merchantId) q.set('merchantId', String(filters.merchantId));
  return adminGet<RiskInterceptPage>(`/risk/intercept-events?${q}`, () => {
    const items = mockIntercepts.filter((item) =>
      (!filters.status || item.status === filters.status) &&
      // mock 存量数据 merchantId 为 number；比较用 String() 归一，不做 Number() 转换
      (!filters.merchantId || String(item.merchantId) === filters.merchantId),
    );
    return { total: items.length, items: items.slice() };
  });
}

export async function getIntercept(id: number | string) {
  return adminGet<RiskInterceptDetail>(`/risk/intercept-events/${encodeURIComponent(id)}`, () => {
    const item = mockIntercepts.find((row) => row.id === Number(id) || row.eventNo === String(id));
    if (!item) throw new Error('拦截事件不存在');
    const advice = mockInterceptDetailAdvice[item.id];
    // 详情响应并列 aiAdviceStatus + aiAdvice（有 mock 建议则带，否则仅基础字段，模拟缺键容错）
    return advice ? { ...item, ...advice } : { ...item };
  });
}

/** 拦截事件处置（敏感写：强制 X-OTP） */
export async function disposeIntercept(
  id: number | string,
  action: RiskDispositionAction,
  comment: string,
  otp: string,
) {
  return adminPost<RiskInterceptDisposeResult>(
    `/risk/intercept-events/${encodeURIComponent(id)}/dispose`,
    { action, comment },
    () => {
      const item = mockIntercepts.find((row) => row.id === Number(id) || row.eventNo === String(id));
      if (!item) throw new Error('拦截事件不存在');
      item.status = action === 'BLOCK' ? 'BLOCKED'
        : action === 'FALSE_ALARM' ? 'FALSE_POSITIVE' : 'RELEASED';
      item.actionTaken = action === 'BLOCK' ? 'BLOCK' : item.actionTaken;
      item.reviewerId = 'mock-admin';
      item.reviewedAt = new Date().toISOString();
      item.reviewComment = comment;
      item.version += 1;
      item.updatedAt = item.reviewedAt;
      return { id: item.id, eventNo: item.eventNo, status: item.status, action, reviewedAt: item.reviewedAt };
    },
    { headers: { 'X-OTP': otp } },
  );
}

export async function listAmlCases(filters: AmlCaseFilters = {}) {
  const q = new URLSearchParams({
    page: String(filters.page ?? 1),
    size: String(filters.size ?? 20),
  });
  if (filters.status) q.set('status', filters.status);
  if (filters.type) q.set('type', filters.type);
  if (filters.merchantId?.trim()) q.set('merchantId', filters.merchantId.trim());
  if (filters.from) q.set('from', filters.from);
  if (filters.to) q.set('to', filters.to);
  return adminGet<AmlCasePage>(`/risk/aml/str-cases?${q}`, () => {
    const rows = mockAml.filter((x) =>
      (!filters.status || x.status === filters.status)
      && (!filters.type || x.type === filters.type)
      && (!filters.merchantId?.trim() || x.merchantId === filters.merchantId.trim()));
    const page = filters.page ?? 1;
    const size = filters.size ?? 20;
    return { total: rows.length, items: rows.slice((page - 1) * size, page * size) };
  });
}

export async function fileAmlCase(
  strId: string,
  body: { comment: string; regulatoryReference: string; regulatoryReceiptJson?: string },
) {
  return adminPost<AmlCase>(
    `/risk/aml/str-cases/${encodeURIComponent(strId)}/file`, body,
    () => {
      const row = mockAml.find((x) => x.strId === strId);
      if (!row || row.status !== 'PENDING') throw new Error('AML 案件状态已变化');
      row.status = 'FILED'; row.decisionComment = body.comment;
      row.regulatoryReference = body.regulatoryReference;
      row.regulatoryReceiptJson = body.regulatoryReceiptJson || null;
      row.reviewerId = 'mock-admin'; row.reviewerName = 'mock-admin';
      row.filedAt = new Date().toISOString(); row.updatedAt = row.filedAt; row.version += 1;
      return { ...row };
    },
  );
}

/** POST /risk/aml/str-cases/{id}/exclude（敏感写：强制 X-OTP） */
export async function excludeAmlCase(strId: string, comment: string, otp: string) {
  return adminPost<AmlCase>(
    `/risk/aml/str-cases/${encodeURIComponent(strId)}/exclude`, { comment },
    () => {
      const row = mockAml.find((x) => x.strId === strId);
      if (!row || row.status !== 'PENDING') throw new Error('AML 案件状态已变化');
      if (row.type === 'CTR') throw new Error('CTR 大额报告不可排除');
      row.status = 'EXCLUDED'; row.decisionComment = comment;
      row.reviewerId = 'mock-admin'; row.reviewerName = 'mock-admin';
      row.excludedAt = new Date().toISOString(); row.updatedAt = row.excludedAt; row.version += 1;
      return { ...row };
    },
    { headers: { 'X-OTP': otp } },
  );
}

export async function listDisputes(filters: DisputeFilters = {}) {
  const q = new URLSearchParams({
    page: String(filters.page ?? 1),
    size: String(filters.size ?? 20),
  });
  if (filters.status) q.set('status', filters.status);
  if (filters.from) q.set('from', filters.from);
  if (filters.to) q.set('to', filters.to);
  if (filters.merchantId) q.set('merchantId', String(filters.merchantId));
  return adminGet<DisputePage>(`/risk/disputes?${q}`, () => {
    const filtered = mockDisputes.filter((d) =>
      (!filters.status || d.status === filters.status) &&
      (!filters.merchantId || d.merchantId === String(filters.merchantId)),
    );
    const offset = ((filters.page ?? 1) - 1) * (filters.size ?? 20);
    return { total: filtered.length, items: filtered.slice(offset, offset + (filters.size ?? 20)) };
  });
}

export async function assignDispute(id: string, assigneeId: string, assigneeName: string) {
  return adminPost<DisputeRow>(
    `/risk/disputes/${encodeURIComponent(id)}/assign`,
    { assigneeId, assigneeName },
    () => {
      const row = mockDisputes.find((d) => d.disputeId === id);
      if (!row) throw new Error('争议案件不存在');
      if (row.status !== 'OPEN') throw new Error('仅 OPEN 案件可分派');
      row.assigneeId = assigneeId;
      row.assignee = assigneeName;
      row.assignedAt = new Date().toISOString();
      row.updatedAt = row.assignedAt;
      row.version += 1;
      return { ...row };
    },
  );
}

export async function investigateDispute(id: string) {
  return adminPost<DisputeRow>(`/risk/disputes/${encodeURIComponent(id)}/investigate`, undefined, () => {
    const row = mockDisputes.find((d) => d.disputeId === id);
    if (!row) throw new Error('争议案件不存在');
    if (row.status !== 'OPEN' || !row.assigneeId) throw new Error('案件须先分派且保持 OPEN');
    row.status = 'INVESTIGATING';
    row.investigatingAt = new Date().toISOString();
    row.updatedAt = row.investigatingAt;
    row.version += 1;
    return { ...row };
  });
}

/** POST /risk/disputes/{id}/resolve（敏感写：强制 X-OTP） */
export async function resolveDispute(id: string, result: 'WON' | 'LOST', comment: string, otp: string) {
  return adminPost<DisputeRow>(
    `/risk/disputes/${encodeURIComponent(id)}/resolve`,
    { result, comment },
    () => {
      const row = mockDisputes.find((d) => d.disputeId === id);
      if (!row) throw new Error('争议案件不存在');
      if (row.status !== 'INVESTIGATING') throw new Error('仅 INVESTIGATING 案件可结案');
      row.status = result;
      row.resolutionComment = comment;
      row.resolvedAt = new Date().toISOString();
      row.updatedAt = row.resolvedAt;
      row.version += 1;
      return { ...row };
    },
    { headers: { 'X-OTP': otp } },
  );
}

export async function list3dsSessions(filters: ThreeDsFilters = {}) {
  const q = new URLSearchParams({
    page: String(filters.page ?? 1),
    size: String(filters.size ?? 20),
  });
  if (filters.status) q.set('status', filters.status);
  if (filters.merchantId?.trim()) q.set('merchantId', filters.merchantId.trim());
  if (filters.from) q.set('from', filters.from);
  if (filters.to) q.set('to', filters.to);
  return adminGet<ThreeDsSessionPage>(`/risk/3ds/sessions?${q}`, () => {
    const rows = mock3ds.filter((row) =>
      (!filters.status || row.status === filters.status)
      && (!filters.merchantId?.trim() || String(row.merchantId) === filters.merchantId.trim())
      && (!filters.from || row.createdAt >= filters.from)
      && (!filters.to || row.createdAt <= filters.to));
    const page = filters.page ?? 1;
    const size = filters.size ?? 20;
    return { total: rows.length, items: rows.slice((page - 1) * size, page * size) };
  });
}

export async function get3dsSession(id: string) {
  return adminGet<ThreeDsSession>(`/risk/3ds/sessions/${encodeURIComponent(id)}`, () => {
    const row = mock3ds.find((session) => session.sessionId === id || String(session.id) === id);
    if (!row) throw new Error('3DS 会话不存在');
    return { ...row };
  });
}

/** POST /risk/3ds/sessions/export（敏感写：强制 X-OTP） */
export async function export3dsFailures(
  filters: Omit<ThreeDsFilters, 'status' | 'page' | 'size'>,
  otp: string,
) {
  const q = new URLSearchParams();
  if (filters.merchantId?.trim()) q.set('merchantId', filters.merchantId.trim());
  if (filters.from) q.set('from', filters.from);
  if (filters.to) q.set('to', filters.to);
  const suffix = q.size ? `?${q}` : '';
  return adminPost<ThreeDsExport>(`/risk/3ds/sessions/export${suffix}`, undefined, () => {
    const rows = mock3ds.filter((row) =>
      (row.status === 'FAILED' || row.status === 'TIMEOUT')
      && (!filters.merchantId?.trim() || String(row.merchantId) === filters.merchantId.trim())
      && (!filters.from || row.createdAt >= filters.from)
      && (!filters.to || row.createdAt <= filters.to));
    const header = 'sessionId,merchantId,merchantName,panMasked,amount,currency,status,acsHost,resultCode,errorCode,createdAt';
    const csv = [header, ...rows.map((row) => [row.sessionId, row.merchantId,
      row.merchantName, row.panMasked, row.amount, row.currency, row.status,
      row.acsHost ?? '', row.resultCode ?? '', row.errorCode ?? '', row.createdAt]
      .map((value) => `"${String(value).split('"').join('""')}"`).join(','))].join('\n');
    return { contentType: 'text/csv;charset=UTF-8', fileName: 'threeds-failures-mock.csv',
      csv, recordCount: rows.length, generatedAt: new Date().toISOString() };
  }, { headers: { 'X-Confirm': 'EXPORT_FAILURES', 'X-OTP': otp } });
}

/** 浏览器侧触发下载（复用 export 敏感写，透传 otp；下载本身不再叠加 X-OTP） */
export async function download3dsFailures(
  filters: Omit<ThreeDsFilters, 'status' | 'page' | 'size'>,
  otp: string,
) {
  const result = await export3dsFailures(filters, otp);
  if (!result.ok) return result;
  const blob = new Blob([result.data.csv], { type: result.data.contentType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = result.data.fileName;
  link.click();
  URL.revokeObjectURL(url);
  return { ok: true as const, data: result.data.recordCount };
}

export async function listFraudGangs(status?: string, level?: string) {
  const q = new URLSearchParams();
  if (status) q.set('status', status);
  if (level) q.set('level', level);
  const suffix = q.size ? `?${q}` : '';
  return adminGet<FraudGang[]>(`/risk/fraud-gangs${suffix}`, () => mockFraudGangs
    .filter((row) => !status || row.status === status)
    .filter((row) => !level || row.level === level)
    .map((row) => ({ ...row })));
}

/** POST /risk/fraud-gangs/{id}/confirm（敏感写：强制 X-OTP） */
export async function confirmFraudGang(id: string, otp: string) {
  return adminPost<FraudGang>(`/risk/fraud-gangs/${encodeURIComponent(id)}/confirm`, {}, () => {
    const row = mockFraudGangs.find((item) => item.gangId === id);
    if (!row || row.status !== 'OPEN') throw new Error('团伙状态不允许确认');
    row.status = 'CONFIRMED';
    row.confirmedAt = new Date().toISOString();
    row.version += 1;
    return { ...row };
  }, { headers: { 'X-OTP': otp } });
}

/** POST /risk/fraud-gangs/{id}/batch-freeze（敏感写：强制 X-OTP） */
export async function freezeFraudGang(id: string, reason: string, otp: string) {
  return adminPost<FraudGang>(`/risk/fraud-gangs/${encodeURIComponent(id)}/batch-freeze`, { reason }, () => {
    const row = mockFraudGangs.find((item) => item.gangId === id);
    if (!row || row.status !== 'CONFIRMED' || row.frozenAt) throw new Error('团伙状态不允许冻结');
    row.freezeReason = reason;
    row.frozenAt = new Date().toISOString();
    row.version += 1;
    return { ...row };
  }, { headers: { 'X-OTP': otp } });
}

/** POST /risk/fraud-gangs/{id}/close（敏感写：强制 X-OTP） */
export async function closeFraudGang(id: string, otp: string) {
  return adminPost<FraudGang>(`/risk/fraud-gangs/${encodeURIComponent(id)}/close`, {}, () => {
    const row = mockFraudGangs.find((item) => item.gangId === id);
    if (!row || row.status !== 'CONFIRMED') throw new Error('团伙状态不允许关闭');
    row.status = 'CLOSED';
    row.closedAt = new Date().toISOString();
    row.version += 1;
    return { ...row };
  }, { headers: { 'X-OTP': otp } });
}

const RULE_CATEGORIES: AdminRiskRuleCategory[] = [
  'VELOCITY', 'AML', 'OPEN_CARD', 'WITHDRAW', 'DEPOSIT', 'DEVICE', 'GEO', 'MCC',
];
let mockRuleSequence = 920000;
const mockAdminRules: AdminRiskRule[] = [
  {
    ruleId: '900001', ruleCode: 'VELOCITY_SINGLE_TXN', scope: 'GLOBAL', merchantId: null,
    parentRuleId: null, category: 'VELOCITY', ruleType: 'THRESHOLD',
    params: { field: 'amount', operator: '>=', value: 100000, window: 'PER_TX', currency: 'USD' },
    comboLogic: null, ruleGroup: null, effectiveScope: null, priority: 5, status: 'LIVE',
    hitAction: 'REVIEW', editable: true, version: 1, createdBy: 'system', updatedBy: 'system',
    createdAt: '2026-08-09T09:00:00', updatedAt: '2026-08-09T09:00:00',
  },
  {
    ruleId: '900004', ruleCode: 'AML_TXN_GT_10000', scope: 'GLOBAL', merchantId: null,
    parentRuleId: null, category: 'AML', ruleType: 'THRESHOLD',
    params: { field: 'amount', operator: '>', value: 1000000, window: 'PER_TX', currency: 'USD' },
    comboLogic: null, ruleGroup: null, effectiveScope: null, priority: 1, status: 'LIVE',
    hitAction: 'REVIEW', editable: false, version: 1, createdBy: 'system', updatedBy: 'system',
    createdAt: '2026-08-09T09:00:00', updatedAt: '2026-08-09T09:00:00',
  },
  {
    ruleId: '900006', ruleCode: 'OPEN_CARD_KYC_BELOW_L1', scope: 'GLOBAL', merchantId: null,
    parentRuleId: null, category: 'OPEN_CARD', ruleType: 'CUSTOM',
    params: { handler: 'open_card_kyc_baseline', params: { minimumLevel: 'L1' } },
    comboLogic: null, ruleGroup: null, effectiveScope: null, priority: 1, status: 'LIVE',
    hitAction: 'BLOCK', editable: false, version: 1, createdBy: 'system', updatedBy: 'system',
    createdAt: '2026-08-09T09:00:00', updatedAt: '2026-08-09T09:00:00',
  },
];

function cloneAdminRule(rule: AdminRiskRule): AdminRiskRule {
  return { ...rule, params: { ...rule.params } };
}

export async function listAdminRiskRules(filters: AdminRiskRuleFilters = {}) {
  const q = new URLSearchParams({
    page: String(filters.page ?? 1), page_size: String(filters.size ?? 20),
  });
  if (filters.category) q.set('category', filters.category);
  if (filters.scope) q.set('scope', filters.scope);
  if (filters.status) q.set('status', filters.status);
  if (filters.priority) q.set('priority', String(filters.priority));
  if (filters.merchantId?.trim()) q.set('merchantId', filters.merchantId.trim());
  return adminGet<AdminRiskRulePage>(`/risk/rules?${q}`, () => {
    const rows = mockAdminRules.filter((rule) =>
      (!filters.category || rule.category === filters.category)
      && (!filters.scope || rule.scope === filters.scope)
      && (!filters.status || rule.status === filters.status)
      && (!filters.priority || rule.priority === filters.priority)
      && (!filters.merchantId?.trim() || rule.merchantId === filters.merchantId.trim()));
    const page = filters.page ?? 1; const size = filters.size ?? 20;
    return {
      total: rows.length, page, size,
      items: rows.slice((page - 1) * size, page * size).map(cloneAdminRule),
      categories: RULE_CATEGORIES.map((category) => ({
        category,
        liveCount: mockAdminRules.filter((r) => r.category === category && r.status === 'LIVE').length,
        totalCount: mockAdminRules.filter((r) => r.category === category).length,
      })),
    };
  });
}

/** POST /risk/rules（敏感写：强制 X-OTP） */
export function createAdminRiskRule(body: Required<Pick<AdminRiskRuleWrite,
  'ruleCode' | 'category' | 'ruleType' | 'params' | 'priority' | 'hitAction'>> & AdminRiskRuleWrite, otp: string) {
  return adminPost<AdminRiskRule>('/risk/rules', body, () => {
    if (mockAdminRules.some((rule) => rule.ruleCode === body.ruleCode)) throw new Error('规则编码已存在');
    const now = new Date().toISOString();
    const rule: AdminRiskRule = {
      ruleId: String(++mockRuleSequence), ruleCode: body.ruleCode, scope: 'GLOBAL',
      merchantId: null, parentRuleId: null, category: body.category, ruleType: body.ruleType,
      params: body.params, comboLogic: body.comboLogic ?? null, ruleGroup: body.ruleGroup ?? null,
      effectiveScope: body.effectiveScope ?? null, priority: body.priority,
      status: body.status ?? 'DRAFT', hitAction: body.hitAction,
      editable: body.editable ?? !['AML', 'OPEN_CARD'].includes(body.category), version: 1,
      createdBy: 'mock-admin', updatedBy: 'mock-admin', createdAt: now, updatedAt: now,
    };
    mockAdminRules.unshift(rule); return cloneAdminRule(rule);
  }, { headers: { 'X-OTP': otp } });
}

/** PUT /risk/rules/{id}（敏感写：强制 X-OTP） */
export function updateAdminRiskRule(rule: AdminRiskRule, body: AdminRiskRuleWrite, otp: string) {
  return adminPut<AdminRiskRule>(`/risk/rules/${encodeURIComponent(rule.ruleId)}`, body, () => {
    const stored = mockAdminRules.find((row) => row.ruleId === rule.ruleId);
    if (!stored || stored.version !== rule.version) throw new Error('规则版本冲突');
    Object.assign(stored, body, { version: stored.version + 1, updatedAt: new Date().toISOString() });
    return cloneAdminRule(stored);
  }, { headers: { 'If-Match': String(rule.version), 'X-OTP': otp } });
}

export function changeAdminRiskRuleStatus(rule: AdminRiskRule, status: AdminRiskRuleStatus) {
  return adminPatch<AdminRiskRule>(`/risk/rules/${encodeURIComponent(rule.ruleId)}/status`, { status }, () => {
    const stored = mockAdminRules.find((row) => row.ruleId === rule.ruleId);
    if (!stored || stored.version !== rule.version) throw new Error('规则版本冲突');
    if (stored.status === 'SHADOW' && status === 'LIVE') throw new Error('须走 promote 预检');
    stored.status = status; stored.version += 1; stored.updatedAt = new Date().toISOString();
    return cloneAdminRule(stored);
  }, { headers: { 'If-Match': String(rule.version), 'X-Confirm': 'true' } });
}

export function promoteAdminRiskRule(rule: AdminRiskRule, otp: string) {
  return adminPost<AdminRiskRulePromote>(
    `/risk/rules/${encodeURIComponent(rule.ruleId)}/promote`, undefined,
    () => {
      const stored = mockAdminRules.find((row) => row.ruleId === rule.ruleId);
      if (!stored || stored.status !== 'SHADOW') throw new Error('仅 SHADOW 可升级');
      stored.status = 'LIVE'; stored.version += 1; stored.updatedAt = new Date().toISOString();
      return { ruleId: stored.ruleId, hitRate: '2.00%', falsePositiveRate: '0.10%',
        sampleSize: 1000, hitCount: 20, passed: true, status: 'LIVE', version: stored.version };
    }, { headers: { 'X-OTP': otp } },
  );
}

/** POST /risk/rules/{id}/rollback（敏感写：强制 X-OTP） */
export function rollbackAdminRiskRule(rule: AdminRiskRule, targetVersion: number, otp: string) {
  return adminPost<AdminRiskRule>(
    `/risk/rules/${encodeURIComponent(rule.ruleId)}/rollback`, { targetVersion },
    () => {
      const stored = mockAdminRules.find((row) => row.ruleId === rule.ruleId);
      if (!stored || targetVersion < 1 || targetVersion >= stored.version) throw new Error('目标版本不存在');
      stored.version += 1; stored.status = 'DRAFT'; stored.updatedAt = new Date().toISOString();
      return cloneAdminRule(stored);
    }, { headers: { 'X-Confirm': 'true', 'X-OTP': otp } },
  );
}

/** DELETE /risk/rules/{id}（敏感写：强制 X-OTP） */
export function deleteAdminRiskRule(rule: AdminRiskRule, otp: string) {
  return adminDelete<{ ruleId: string; deleted: boolean }>(
    `/risk/rules/${encodeURIComponent(rule.ruleId)}`,
    () => {
      const index = mockAdminRules.findIndex((row) => row.ruleId === rule.ruleId);
      if (index < 0 || !['DRAFT', 'DISABLED'].includes(mockAdminRules[index].status)) {
        throw new Error('仅 DRAFT/DISABLED 可删除');
      }
      mockAdminRules.splice(index, 1); return { ruleId: rule.ruleId, deleted: true };
    }, { headers: { 'If-Match': String(rule.version), 'X-Confirm': 'true', 'X-OTP': otp } },
  );
}

/** POST /risk/rules/kill-switch（敏感写：强制 X-OTP） */
export function killSwitchAdminRiskRules(otp: string) {
  return adminPost<{ affectedCount: number; executedAt: string }>(
    '/risk/rules/kill-switch', { action: 'DISABLE_ALL_MERCHANT_OVERRIDE' },
    () => {
      let affectedCount = 0;
      mockAdminRules.forEach((rule) => {
        if (rule.scope === 'MERCHANT' && rule.status !== 'DISABLED') {
          rule.status = 'DISABLED'; rule.version += 1; affectedCount += 1;
        }
      });
      return { affectedCount, executedAt: new Date().toISOString() };
    }, { headers: { 'X-Confirm': 'true', 'X-OTP': otp } },
  );
}

export function listRiskBlocklist(filters: RiskBlocklistFilters = {}) {
  const q = new URLSearchParams({
    page: String(filters.page ?? 1), pageSize: String(filters.pageSize ?? 20),
  });
  if (filters.listType) q.set('listType', filters.listType);
  if (filters.targetType) q.set('targetType', filters.targetType);
  if (filters.source) q.set('source', filters.source);
  if (filters.expiry) q.set('expiry', filters.expiry);
  if (filters.q?.trim()) q.set('q', filters.q.trim());
  return adminGet<RiskBlocklistPage>(`/risk/blocklist?${q}`, () => {
    const now = Date.now();
    const rows = mockBlocklist.filter((entry) => {
      const expired = !!entry.expiresAt && new Date(entry.expiresAt).getTime() <= now;
      const expiryMatches = !filters.expiry
        || (filters.expiry === 'PERMANENT' && !entry.expiresAt)
        || (filters.expiry === 'ACTIVE' && !!entry.expiresAt && !expired)
        || (filters.expiry === 'EXPIRED' && expired);
      const keyword = filters.q?.trim().toLowerCase();
      return (!filters.listType || entry.listType === filters.listType)
        && (!filters.targetType || entry.targetType === filters.targetType)
        && (!filters.source || entry.source === filters.source)
        && expiryMatches
        && (!keyword || entry.targetValue.toLowerCase().includes(keyword)
          || entry.reason.toLowerCase().includes(keyword));
    }).map((entry) => ({
      ...entry, expired: !!entry.expiresAt && new Date(entry.expiresAt).getTime() <= now,
    }));
    const page = filters.page ?? 1; const pageSize = filters.pageSize ?? 20;
    return { total: rows.length, page, pageSize,
      items: rows.slice((page - 1) * pageSize, page * pageSize) };
  });
}

/** POST /risk/blocklist（敏感写：强制 X-OTP） */
export function addRiskBlocklist(body: RiskBlocklistWrite, otp: string) {
  return adminPost<RiskBlocklistWriteResult>('/risk/blocklist', body, () => {
    const targetValue = body.targetType === 'COUNTRY' ? body.targetValue.trim().toUpperCase() : body.targetValue.trim();
    if (mockBlocklist.some((entry) => entry.listType === body.listType
      && entry.targetType === body.targetType && entry.targetValue === targetValue)) {
      return { successCount: 0, skippedCount: 1, failCount: 0, errors: [] };
    }
    const now = new Date().toISOString();
    mockBlocklist.unshift({
      listId: String(++mockBlocklistSequence), listType: body.listType, targetType: body.targetType,
      targetValue, source: 'MANUAL', reason: body.reason.trim(), createdBy: 'mock-admin',
      expiresAt: body.expiresAt || null,
      expired: !!body.expiresAt && new Date(body.expiresAt).getTime() <= Date.now(), createdAt: now,
    });
    return { successCount: 1, skippedCount: 0, failCount: 0, errors: [] };
  }, { headers: { 'X-OTP': otp } });
}

/** POST /risk/blocklist（multipart CSV 批量导入；敏感写：强制 X-OTP） */
export function uploadRiskBlocklistCsv(file: File, otp: string) {
  const form = new FormData();
  form.append('file', file);
  return adminUpload<RiskBlocklistWriteResult>('/risk/blocklist', form, () => ({
    successCount: 2, skippedCount: 1, failCount: 1,
    errors: [{ line: 4, reason: 'MCC targetValue 必须为 4 位数字' }],
  }), { headers: { 'X-OTP': otp } });
}

/** DELETE /risk/blocklist/{listId}（敏感写：强制 X-OTP） */
export function deleteRiskBlocklist(entry: RiskBlocklistEntry, otp: string) {
  return adminDelete<{ listId: string; deleted: boolean }>(
    `/risk/blocklist/${encodeURIComponent(entry.listId)}`,
    () => {
      if (entry.source === 'RULE_AUTO') throw new Error('规则联动名单不可手工删除');
      const index = mockBlocklist.findIndex((row) => row.listId === entry.listId);
      if (index >= 0) mockBlocklist.splice(index, 1);
      return { listId: entry.listId, deleted: true };
    },
    { headers: { 'X-OTP': otp } },
  );
}

export function downloadRiskBlocklistTemplate() {
  return adminDownload('/risk/blocklist/import/template', () => ({
    fileName: 'risk-blocklist-template.csv',
    blob: new Blob([
      'target_type,target_value,list_type,expires_at,reason\nIP,1.2.3.4,BLACK,,manual block\n',
    ], { type: 'text/csv;charset=utf-8' }),
  }));
}

export { USE_MOCK };
